import React, { useMemo, useState, useEffect } from 'react';
import useProductsList from '../products-list/useProductList.api';
import useBookingsList from '~/apis/useBookingsList.api';
import { UISelect } from '~/components/common/forms';
import Pagination from '~/components/common/pagination';
import { Translate, useTranslate } from '~/i18n';
import AttendeesTable from './attendees-table';
import useTimeSlots from '~/apis/useTimeSlots';
import { useRouter } from 'next/router';
import { ProductProps } from '~/interfaces/product';
import { components, ControlProps } from 'react-select';
import { CalendarIconOutline, ClockIconOutline, FilterIconOutline, FileIcon } from '~/svgs';
import { Button } from '~/components/common/button';
import { SubmitLoader } from '~/components/common/loaders/submit';
import useDownloadAttendees from '~/apis/download-attendees-list';

type AttendeesListType = {
   hideProductFilter?: boolean;
   product?: ProductProps;
};

const FilterIcon = ({ children, ...props }: ControlProps) => {
   return (
      <components.Control {...props}>
         <div className="ltr:pl-4 rtl:pr-4">
            <FilterIconOutline />
         </div>
         {children}
      </components.Control>
   );
};

const DateIcon = ({ children, ...props }: ControlProps) => {
   return (
      <components.Control {...props}>
         <div className="ltr:pl-4 rtl:pr-4">
            <CalendarIconOutline />
         </div>
         {children}
      </components.Control>
   );
};

const TimeIcon = ({ children, ...props }: ControlProps) => {
   return (
      <components.Control {...props}>
         <div className="ltr:pl-4 rtl:pr-4">
            <ClockIconOutline />
         </div>
         {children}
      </components.Control>
   );
};

const AttendeesList = ({ hideProductFilter = false, product }: AttendeesListType) => {
   const { translate, lang } = useTranslate();
   const router = useRouter();
   const [productFilters, setProductFilters] = useState(router.query.id ? router.query.id : '');
   const [time, setTime] = useState<string | null>();
   const [productUnit, setProductUnit] = useState<string | null>(
      product?.duration_unit ? product?.duration_unit : null
   );
   const [pageNumber, setPageNumber] = useState(1);
   const [date, setDate] = useState<string | null>(null);
   const productInMinutes = productUnit === 'minutes';
   const { data: timeSlots } = useTimeSlots(productFilters);
   const { data: productList } = useProductsList({
      statusFilter: 'Active',
      for_attendees: true,
   });
   const { data, isLoading } = useBookingsList({
      pageNumber,
      // statusFilter: ['Paid'],
      product: productFilters,
      time: productInMinutes ? time : null,
      date: date,
   });
   const productListOptions = useMemo(() => {
      return productList?.data?.map(({ _id, name, duration_unit }: any) => ({
         value: _id,
         label: name[lang] || name.en,
         unit: duration_unit,
      }));
   }, [productList, lang]);

   const timesListOptions = useMemo(() => {
      return timeSlots?.data
         ?.map(({ filter_date, filter_date_value, filter_time, filter_time_value }: any) => ({
            value: filter_time_value,
            label: filter_date ? `${filter_date}  ${filter_time}` : filter_time,
            date: filter_date_value,
         }))
         ?.sort((t1: any, t2: any) => Date.parse(t2.date) - Date.parse(t1.date));
   }, [timeSlots]);

   const datesListOptions = useMemo(() => {
      return timeSlots?.data?.map(({ filter_date, filter_date_value }: any) => ({
         value: filter_date_value,
         label: filter_date,
      }));
   }, [timeSlots]);

   const { isFetching: isDownLoading, refetch: refetchDownloadAttendees } = useDownloadAttendees({
      lang: lang,
      productID: Array.isArray(productFilters) ? productFilters[0] : productFilters,
   });

   useEffect(() => {
      setTime(null);
      setDate(null);
   }, [productFilters]);

   return (
      <div>
         <div className="flex flex-col xs:flex-row justify-between xs:items-center gap-2 my-4">
            <div className="flex flex-col xs:flex-row xs:space-x-2 rtl:space-x-reverse gap-x-6">
               {!hideProductFilter && (
                  <UISelect
                     label={translate({ id: 'operations:filter by' })}
                     onChange={({ value, unit }: any) => {
                        setProductFilters(value);
                        setProductUnit(unit);
                     }}
                     options={productListOptions}
                     components={{ Control: FilterIcon }}
                     placeholder={translate({ id: 'operations:filter by product' })}
                     className="w-full xs:!w-72"
                  />
               )}
               {productUnit ? (
                  productInMinutes ? (
                     <UISelect
                        label={translate({ id: 'common:time' })}
                        onChange={({ value }: any) => {
                           setTime(value);
                           setDate(value.split(' ')[0]);
                        }}
                        placeholder={translate({ id: 'operations:start time' })}
                        value={timesListOptions?.filter((item: any) => item.value === time)}
                        options={timesListOptions}
                        components={{ Control: TimeIcon }}
                        className="w-full xs:!w-72"
                     />
                  ) : (
                     <UISelect
                        label={translate({ id: 'common:date' })}
                        onChange={({ value }: any) => setDate(value)}
                        value={datesListOptions?.filter((item: any) => item.value === date)}
                        placeholder={translate({ id: 'createproduct:set a date' })}
                        options={datesListOptions}
                        components={{ Control: DateIcon }}
                        className="w-full xs:!w-72"
                     />
                  )
               ) : null}
            </div>
            <Button
               disabled={!productUnit || !data?.data.length || isDownLoading}
               className="!w-full xs:!w-72 xs:mt-6 h-12 gap-3 group"
               theme="outline-primary"
               onClick={() => refetchDownloadAttendees()}>
               {isDownLoading ? (
                  <SubmitLoader width="30" />
               ) : (
                  <>
                     <FileIcon className="[&>*]:fill-primary group-hover:[&>*]:fill-white group-disabled:[&>*]:fill-grey-300" />
                     <Translate id="bookings:export to spreadsheet" />
                  </>
               )}
            </Button>
         </div>
         <AttendeesTable data={data} loading={isLoading} />
         {data && data.total > data.per_page && (
            <div className="flex justify-center">
               <Pagination
                  back={() => setPageNumber(prev => prev - 1)}
                  next={() => setPageNumber(prev => prev + 1)}
                  currentPage={data.curr_page}
                  lastPage={data.last_page}
                  numberOfElementsInOnePage={data.per_page}
                  totalElements={data.total}
               />
            </div>
         )}
      </div>
   );
};

export default AttendeesList;
